/**
 * Secret expiry (Graph client secret) state for warning dots and mail settings banner.
 * Fetches GET /api/graph/secret-expiry once when provider mounts.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';

export type SecretExpirySeverity = 'none' | 'warning' | 'urgent' | 'critical';

export interface SecretExpiryState {
  expiresAt: string | null;
  warning: boolean;
  daysRemaining: number | null;
  severity: SecretExpirySeverity;
  loading: boolean;
}

const defaultState: SecretExpiryState = {
  expiresAt: null,
  warning: false,
  daysRemaining: null,
  severity: 'none',
  loading: true,
};

const SecretExpiryContext = createContext<SecretExpiryState>(defaultState);

export const SecretExpiryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SecretExpiryState>(defaultState);

  useEffect(() => {
    if (!API_BASE_URL) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    fetch(`${API_BASE_URL}/api/graph/secret-expiry`)
      .then((res) => res.json())
      .then((data: { expiresAt?: string | null; warning?: boolean; daysRemaining?: number | null; severity?: string }) => {
        setState({
          expiresAt: data.expiresAt ?? null,
          warning: Boolean(data.warning),
          daysRemaining: data.daysRemaining != null ? Number(data.daysRemaining) : null,
          severity: (data.severity === 'critical' || data.severity === 'urgent' || data.severity === 'warning' ? data.severity : 'none') as SecretExpirySeverity,
          loading: false,
        });
      })
      .catch(() => setState((s) => ({ ...s, loading: false })));
  }, []);

  return (
    <SecretExpiryContext.Provider value={state}>
      {children}
    </SecretExpiryContext.Provider>
  );
};

export const useSecretExpiry = (): SecretExpiryState => useContext(SecretExpiryContext);
